import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Livecoding 52: First impressions of Vue",
  "description": "",
  "published": "2017-11-20T00:00:00.000Z",
  "redirect_from": ["/blog/livecoding-52-first-impressions-vue/swizec/7935"],
  "categories": "Livecoding, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should subscribe to `}<a parentName="em" {...{
          "href": "https://www.youtube.com/TheSwizec"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>
    <lite-youtube {...{
      "videoid": "MZBVM6zwvkA",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`This weekend I set out to build an idea @TheKitze had on Twitter. An app that gives perspective to your life. Oh, you don't have time to read a book? Well, you spent 5 hours on Twitter yesterday. Are you sure?`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Wish there was an app to put "I don't have enough time" or "this costs too much money" into perspective.`}<br parentName="p"></br><br parentName="p"></br>{`Example: `}<br parentName="p"></br><br parentName="p"></br>{`Me: I don't have enough time to read books.`}<br parentName="p"></br>{`App: You wasted 29 hours on Twitter this week.`}<br parentName="p"></br><br parentName="p"></br>{`Me: 16$ is too much for this book.`}<br parentName="p"></br>{`App: You ate a fucking 25$ burger.`}</p>{`— Kitze (@thekitze) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/thekitze/status/931948509472927744"
      }}>{`November 18, 2017`}</a></blockquote>
    <p>{`For the MVP, I envisioned a form where you fill out what you don't have time or money for, ask for some perspective, and the webapp delivers. It doesn't deliver yet because I decided to build it in `}<a parentName="p" {...{
        "href": "https://vuejs.org/"
      }}>{`Vue`}</a>{`.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/Screen-Shot-2017-11-20-at-09.16.40.png",
        "alt": null
      }}></img></p>
    <p>{`You can see some of my initial struggles with Vue in the video above. In a few bullet points 👇`}</p>
    <ol>
      <li parentName="ol">{`Vue's onboarding experience feels like a warm hug. You're immediately showered in community support, example projects, and helpful resources.`}</li>
      <li parentName="ol">{`vue-cli is more accommodating to different preferences than create-react-app. It asks questions and tries to configure itself for a good experience.`}</li>
      <li parentName="ol">{`The default ESLint config that comes with vue-cli is woefully incompatible with my Prettier config. That was painful. I wish it came with a `}<inlineCode parentName="li">{`.prettierrc`}</inlineCode>{` file so it would configure my autoformatter to the settings it expects.`}</li>
      <li parentName="ol">{`Throwing linting errors instead of warnings by default feels a little forceful. I had to re-make my project from scratch without ESLint.`}</li>
      <li parentName="ol">{`Choosing the right VSCode extension to get Vue support… well, there are a lot of them. I ended up going with the Vue Extension Pack. I don't know everything that it did for me, but I'm sure I'll need it eventually.`}</li>
      <li parentName="ol">{`Out-of-the-box support for end-to-end testing with Nightwatch is 👌`}</li>
      <li parentName="ol">{`The Vue coding experience itself… I think I need to give it more time.`}</li>
    </ol>
    <p>{`Vue feels like a bastard child between React 13 and Angular 1. I love that it uses and promotes components. That's how it should be. I like that it tries to conform to the web-components standard. It's a good standard, and this will help it come about quicker.`}</p>
    <p>{`I hate the Angular 1 style `}<inlineCode parentName="p">{`v-if`}</inlineCode>{` and `}<inlineCode parentName="p">{`v-for`}</inlineCode>{` stuff where you shove a bunch of complex logic into your templates. I know this is hypocritical coming from the React world where we shove our templates into JavaScript, but somehow that feels more elegant.`}</p>
    <p>{`Yes, the React approach does lead to some horrendous code when people put too much logic into their JSX.`}</p>
    <p>{`Yes, it's all about programmer discipline to avoid mixing presentation with logic too much.`}</p>
    <p>{`And yet, I still feel like it's more elegant to treat HTML as a first-class citizen in your JavaScript than to treat JavaScript as a 2nd class citizen in your HTML.`}</p>
    <p>{`With React, it feels like my JavaScript directly manipulates what's on the screen. I'm building a UI and have direct control of what's being shown to the user. I can assign pieces of the UI to different variables, treat them as Just Data™, and generally have fun and feel in control.`}</p>
    <p>{`Vue, on the other hand, enforces an artificial separation of concerns that I don't think is useful. I'm still building a UI and manipulating what's on the screen, but it feels removed. Like trying to tie a knot with 10 foot tweezers instead of your fingers.`}</p>
    <p>{`With React, I can reach in and get dirty. With Vue, my dandy little hands never touch anything real.`}</p>
    <p>{`That's what it feels like anyway. `}<em parentName="p">{`shrug`}</em></p>
    <p>{`And the syntax for building components feels strange too. Very React 13 where we didn't have proper JavaScript class or function support yet.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`export default {
    name: "MyComponent"
    data() {
        return {
            data1: "",
            data2: ""
        }
    },
    methods: {
        doThing() {
        },

        onClick() {
        }
    },
    components: {
        ChildComponent
        AnotherChild
    }
}
`}</code></pre>
    <p>{`o.O`}</p>
    <p>{`That, to me, looks weird. Where are all the advances we've made in JavaScript over the past few years?`}</p>
    <p>{`Why am I defining child `}<inlineCode parentName="p">{`components`}</inlineCode>{` with an object, internal state, called `}<inlineCode parentName="p">{`data`}</inlineCode>{`, with a function, and a list of `}<inlineCode parentName="p">{`methods`}</inlineCode>{` as what looks almost like a class but isn't quite? Feels weird.`}</p>
    <p>{`I like the React approach where you have a JavaScript class. Its methods are its methods, its props can be state, there's a `}<inlineCode parentName="p">{`.state`}</inlineCode>{` variable which is the proper connected state, and children are automagically defined by being nested in the component. You know, just like in normal HTML.`}</p>
    <p>{`Wouldn't it be weird if in HTML you had to declare all the children you're going to use in a certain element?`}</p>
    <p>{`So far I am more weirded out than impressed. Perhaps with time Vue will grow on me.`}</p>
    <p>{`🤓`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      